import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Package, ShoppingBag, MessageCircle, Download, Search, Play, Phone, MapPin, Building2, Share2, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

// GST is charged over and above the listed price; change the rate here only
const GST_NOTE = '+18% GST extra';

const formatPrice = (price) => {
  const n = Number(price);
  const opts = Number.isInteger(n) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return `₹${n.toLocaleString('en-IN', opts)}`;
};

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [settings, setSettings] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedProductView, setSelectedProductView] = useState(null);
  const [imageIndexes, setImageIndexes] = useState({}); // Track current image index for each product
  const navigate = useNavigate();
  const { catalogueType } = useParams();

  // Image navigation functions
  const nextImage = (productId, totalImages, e) => {
    e.stopPropagation();
    setImageIndexes(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % totalImages
    }));
  };

  const prevImage = (productId, totalImages, e) => {
    e.stopPropagation();
    setImageIndexes(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const getImageUrl = (img) => {
    if (img.startsWith('data:')) return img;
    if (img.includes('/uploads/')) {
      const filename = img.split('/uploads/').pop();
      return `${API.replace('/api', '')}/uploads/${filename}`;
    }
    return `${API.replace('/api', '')}/uploads/${img}`;
  };

  // Catalogue display names
  const catalogueNames = {
    'post_print': 'Post Print Catalogue',
    'gift_items': 'Customize Gift Items'
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchSettings();
  }, [catalogueType]);

  useEffect(() => {
    filterProducts();
  }, [selectedCategory, searchQuery, products]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?catalogue_type=${catalogueType || 'post_print'}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Only fetch published products for public view, filtered by catalogue type
      const response = await axios.get(`${API}/products?status=published&catalogue_type=${catalogueType || 'post_print'}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  };

  const handleWhatsAppClick = (product) => {
    if (!settings?.whatsapp_number) {
      toast.error('WhatsApp number not configured');
      return;
    }
    const message = `Hi, I'm interested in ${product.name} (${formatPrice(product.price)} ${GST_NOTE})`;
    const whatsappUrl = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareProduct = (product) => {
    const category = getCategoryName(product.category_id);

    // Get the image URL for WhatsApp preview
    let imageUrl = '';
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage.startsWith('http')) {
        imageUrl = firstImage;
      } else if (!firstImage.startsWith('data:')) {
        // Use the actual server URL for WhatsApp sharing
        const serverUrl = 'https://united-catalogue.com';
        imageUrl = `${serverUrl}/uploads/${firstImage.replace('/uploads/', '').replace('uploads/', '')}`;
      }
    }

    // Create a formatted message with product details
    let message = '';

    // Add image URL at the very beginning for WhatsApp preview
    if (imageUrl) {
      message = `${imageUrl}\n\n`;
    }

    message += `🛒 *${product.name}* from United Copier\n\n`;
    message += `💰 Price: ₹${product.price.toLocaleString('en-IN')} (${GST_NOTE})\n`;
    message += `📁 Category: ${category}\n\n`;

    // Add description (truncate if too long)
    const shortDesc = product.description.length > 100
      ? product.description.substring(0, 100) + '...'
      : product.description;
    message += `📝 ${shortDesc}\n\n`;

    // Add website link
    message += `🌐 Visit our catalogue: https://united-catalogue.com\n\n`;

    // Add contact info
    message += `📞 Contact: ${settings?.whatsapp_number || '8103349299'}\n`;
    message += `📍 United Copier - All Solutions Under A Roof for Printers`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp to share product');
  };

  const handleGeneratePDF = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    try {
      const response = await axios.post(`${API}/generate-pdf`, {
        product_ids: selectedProducts
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'catalogue.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully!');
      setPdfDialogOpen(false);
      setSelectedProducts([]);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;

    // Handle different YouTube URL formats
    let videoId = null;

    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    // Format: https://youtube.com/watch?v=VIDEO_ID
    if (url.includes('watch?v=')) {
      videoId = url.split('watch?v=')[1].split('&')[0];
    }
    // Format: https://youtu.be/VIDEO_ID
    else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    }
    // Format: https://www.youtube.com/embed/VIDEO_ID
    else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1].split('?')[0];
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const formatDescription = (text) => {
    if (!text) return '';

    // Split into lines
    const lines = text.split('\n');
    let formattedHtml = '';
    let inList = false;

    lines.forEach((line, index) => {
      // Skip empty lines but add spacing
      if (line.trim() === '') {
        if (inList) {
          formattedHtml += '</ul>';
          inList = false;
        }
        formattedHtml += '<br/>';
        return;
      }

      // Handle bullet points (lines starting with - or * or •)
      if (line.trim().match(/^[-*•]\s/)) {
        if (!inList) {
          formattedHtml += '<ul class="list-disc ml-5 space-y-1">';
          inList = true;
        }
        const content = line.trim().substring(2); // Remove the bullet marker
        formattedHtml += `<li>${formatInlineStyles(content)}</li>`;
      } else {
        // Close list if we were in one
        if (inList) {
          formattedHtml += '</ul>';
          inList = false;
        }
        // Regular paragraph
        formattedHtml += `<p class="mb-2">${formatInlineStyles(line)}</p>`;
      }
    });

    // Close list if still open
    if (inList) {
      formattedHtml += '</ul>';
    }

    return formattedHtml;
  };

  const formatInlineStyles = (text) => {
    // Bold: **text** or __text__
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_ (but not if it's part of **)
    text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    text = text.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');

    return text;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-blue-950 sticky top-0 z-50 shadow-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col gap-3">
            {/* Back button and Logo */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-blue-100 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back to Home</span>
              </Button>
              <div className="flex-1 flex items-center justify-center">
                {settings?.company_logo && (
                  <div className="bg-white rounded-md px-4 py-1.5 shadow-sm">
                  <img
                    src={(() => {
                      const logo = settings.company_logo;
                      if (logo.startsWith('data:')) return logo;
                      if (logo.includes('/uploads/')) {
                        const filename = logo.split('/uploads/').pop();
                        return `${API.replace('/api', '')}/uploads/${filename}`;
                      }
                      return `${API.replace('/api', '')}/uploads/${logo}`;
                    })()}
                    alt="United Copier Logo"
                    className="h-12 md:h-14 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  </div>
                )}
              </div>
              <div className="w-[100px]"></div> {/* Spacer for centering */}
            </div>
            {/* Buttons - Centered */}
            <div className="flex items-center justify-center space-x-2 md:space-x-3">
              <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="generate-pdf-btn" className="bg-white text-blue-950 hover:bg-blue-50 font-semibold shadow-sm">
                    <Download className="mr-2 h-4 w-4" />
                    <span className="hidden md:inline">Download</span> PDF
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Select Products for PDF Brochure</DialogTitle>
                    <DialogDescription>Choose the products you want to include in the PDF</DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-2">
                      {products.map(product => (
                        <div key={product.id} className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-blue-900 transition-all">
                          <Checkbox
                            data-testid={`pdf-select-${product.id}`}
                            checked={selectedProducts.includes(product.id)}
                            onCheckedChange={() => toggleProductSelection(product.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{product.name}</p>
                            <p className="text-sm text-slate-600">{formatPrice(product.price)} <span className="text-xs text-slate-400">{GST_NOTE}</span></p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="flex justify-between items-center pt-4 border-t">
                    <p className="text-sm text-slate-600">{selectedProducts.length} products selected</p>
                    <Button data-testid="download-pdf-btn" onClick={handleGeneratePDF} disabled={selectedProducts.length === 0} className="bg-blue-900 hover:bg-blue-800 text-white">
                      <Download className="mr-2 h-4 w-4" />
                      Generate PDF
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Button data-testid="admin-login-btn" variant="outline" onClick={() => navigate('/admin')} className="border border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white">
                Admin
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Search and Filter */}
      <div className="container mx-auto px-4 py-6 md:py-8">
        <h1 className="text-3xl md:text-4xl font-bold text-center text-slate-900 tracking-tight mb-2">
          {catalogueNames[catalogueType] || 'Product Catalogue'}
        </h1>
        <div className="w-16 h-1 bg-blue-900 mx-auto mb-4 rounded-full"></div>
        <p className="text-center text-slate-600 mb-6 text-sm md:text-base">
          {catalogueType === 'gift_items'
            ? 'Explore our customizable gift and promotional items'
            : 'Browse our complete range of printer consumables and accessories'}
        </p>
        <div className="flex flex-col md:flex-row gap-4 mb-6 md:mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border border-slate-300 focus:border-blue-900 shadow-sm"
            />
          </div>
        </div>

        {/* Category Filter - Dropdown on Mobile, Tabs on Desktop */}

        {/* Mobile Dropdown */}
        <div className="mb-6 md:hidden">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full bg-white border border-slate-300 shadow-sm h-12 text-base">
              <SelectValue placeholder="Select Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-base py-3">
                <div className="flex items-center">
                  <Package className="mr-2 h-4 w-4" />
                  All Products
                </div>
              </SelectItem>
              {categories.map(category => (
                <SelectItem key={category.id} value={category.id} className="text-base py-3">
                  <div className="flex items-center">
                    <Package className="mr-2 h-4 w-4" />
                    {category.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-8 hidden md:block">
          <TabsList className="bg-white border border-slate-200 p-1 shadow-sm">
            <TabsTrigger data-testid="category-all" value="all" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              All Products
            </TabsTrigger>
            {categories.map(category => (
              <TabsTrigger
                data-testid={`category-${category.id}`}
                key={category.id}
                value={category.id}
                className="data-[state=active]:bg-blue-900 data-[state=active]:text-white"
              >
                {category.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package className="mx-auto h-16 w-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map(product => (
              <Card key={product.id} className="product-card overflow-hidden bg-white border border-slate-200 hover:border-blue-900 shadow-sm" data-testid={`product-card-${product.id}`}>
                <div className="relative h-56 bg-white border-b border-slate-100 overflow-hidden group">
                  {product.images && product.images.length > 0 ? (
                    <>
                      <img
                        src={getImageUrl(product.images[imageIndexes[product.id] || 0])}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                        }}
                      />
                      {/* Image Navigation Arrows */}
                      {product.images.length > 1 && (
                        <>
                          <button
                            onClick={(e) => prevImage(product.id, product.images.length, e)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          >
                            <ChevronLeft className="h-5 w-5 text-slate-700" />
                          </button>
                          <button
                            onClick={(e) => nextImage(product.id, product.images.length, e)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          >
                            <ChevronRight className="h-5 w-5 text-slate-700" />
                          </button>
                          {/* Image Counter */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                            {(imageIndexes[product.id] || 0) + 1} / {product.images.length}
                          </div>
                        </>
                      )}
                    </>
                  ) : null}
                  <div className="fallback-icon w-full h-full flex items-center justify-center" style={{ display: product.images && product.images.length > 0 ? 'none' : 'flex' }}>
                    <Package className="h-20 w-20 text-slate-200" />
                  </div>
                  <Badge className="absolute top-3 right-3 bg-blue-950 text-white border-0 shadow-sm z-20">
                    {getCategoryName(product.category_id)}
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 font-bold tracking-tight">{product.name}</CardTitle>
                  <CardDescription
                    className="text-slate-600 line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: formatDescription(product.description) }}
                  />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-blue-900">{formatPrice(product.price)}</span>
                        <p className="text-xs text-slate-500 font-medium">{GST_NOTE}</p>
                      </div>
                      <Button
                        data-testid={`share-btn-${product.id}`}
                        onClick={() => handleShareProduct(product)}
                        size="icon"
                        variant="outline"
                        className="border border-green-500 text-green-600 hover:bg-green-50"
                        title="Share on WhatsApp"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`whatsapp-btn-${product.id}`}
                        onClick={() => handleWhatsAppClick(product)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white shadow-sm"
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Contact
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button data-testid={`view-details-btn-${product.id}`} variant="outline" className="flex-1 border border-blue-900 text-blue-900 hover:bg-blue-50">
                            View Details
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-2xl text-slate-900 font-bold tracking-tight">{product.name}</DialogTitle>
                            <DialogDescription>View complete product details</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            {/* Image Gallery with Main Image and Thumbnails */}
                            {product.images && product.images.length > 0 && (
                              <div className="space-y-3">
                                {/* Main Image with Navigation */}
                                <div className="relative bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                                  <img
                                    src={getImageUrl(product.images[imageIndexes[`modal-${product.id}`] || 0])}
                                    alt={product.name}
                                    className="w-full h-64 md:h-80 object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  {product.images.length > 1 && (
                                    <>
                                      <button
                                        onClick={(e) => prevImage(`modal-${product.id}`, product.images.length, e)}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                                      >
                                        <ChevronLeft className="h-6 w-6 text-slate-700" />
                                      </button>
                                      <button
                                        onClick={(e) => nextImage(`modal-${product.id}`, product.images.length, e)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all"
                                      >
                                        <ChevronRight className="h-6 w-6 text-slate-700" />
                                      </button>
                                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                                        {(imageIndexes[`modal-${product.id}`] || 0) + 1} / {product.images.length}
                                      </div>
                                    </>
                                  )}
                                </div>
                                {/* Thumbnail Grid */}
                                {product.images.length > 1 && (
                                  <div className="grid grid-cols-5 md:grid-cols-8 gap-2">
                                    {product.images.map((img, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => setImageIndexes(prev => ({ ...prev, [`modal-${product.id}`]: idx }))}
                                        className={`aspect-square rounded-lg border-2 overflow-hidden transition-all ${
                                          (imageIndexes[`modal-${product.id}`] || 0) === idx
                                            ? 'border-blue-900 ring-2 ring-blue-200'
                                            : 'border-slate-200 hover:border-blue-900'
                                        }`}
                                      >
                                        <img
                                          src={getImageUrl(img)}
                                          alt={`${product.name} ${idx + 1}`}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            <div>
                              <p className="text-sm text-slate-600 mb-1">Category</p>
                              <Badge className="bg-blue-950 text-white">{getCategoryName(product.category_id)}</Badge>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600 mb-1">Price</p>
                              <p className="text-3xl font-bold text-blue-900">{formatPrice(product.price)}</p>
                              <p className="text-sm text-slate-500 font-medium mt-0.5">{GST_NOTE}</p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-600 mb-2 font-semibold">Description</p>
                              <div
                                className="text-slate-700 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: formatDescription(product.description) }}
                              />
                            </div>
                            {product.youtube_link && getYouTubeEmbedUrl(product.youtube_link) && (
                              <div>
                                <p className="text-sm text-slate-600 mb-2">Video</p>
                                <div className="aspect-video">
                                  <iframe
                                    width="100%"
                                    height="100%"
                                    src={getYouTubeEmbedUrl(product.youtube_link)}
                                    title="Product video"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="rounded-lg border border-slate-200"
                                  ></iframe>
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button
                                data-testid={`whatsapp-modal-btn-${product.id}`}
                                onClick={() => handleWhatsAppClick(product)}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white shadow-sm"
                              >
                                <MessageCircle className="mr-2 h-4 w-4" />
                                Contact via WhatsApp
                              </Button>
                              <Button
                                data-testid={`share-modal-btn-${product.id}`}
                                onClick={() => handleShareProduct(product)}
                                variant="outline"
                                className="flex-1 border border-green-500 text-green-600 hover:bg-green-50"
                              >
                                <Share2 className="mr-2 h-4 w-4" />
                                Share Product
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-blue-950 text-white mt-16">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div>
              <h3 className="text-2xl font-bold mb-4">United Copier</h3>
              <p className="text-slate-300 mb-2">All Solutions Under A Roof for Printers</p>
              <div className="space-y-2 text-slate-300">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 mr-2 mt-1 flex-shrink-0" />
                  <span>118, Jaora Compound, Indore</span>
                </div>
                <div className="flex items-center">
                  <Phone className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>8103349299</span>
                </div>
              </div>
            </div>

            {/* Branch Offices */}
            <div>
              <h3 className="text-xl font-bold mb-4">Branch Offices</h3>
              <div className="space-y-3 text-slate-300">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>Bhopal</span>
                </div>
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span>Jabalpur</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="block text-slate-300 hover:text-white transition-colors"
                >
                  Products
                </button>
                {settings?.whatsapp_number && (
                  <a 
                    href={`https://wa.me/${settings.whatsapp_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-slate-300 hover:text-white transition-colors"
                  >
                    Contact Us
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-6 text-center text-slate-400">
            <p>&copy; {new Date().getFullYear()} United Copier. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;